import type { IConversationMessage } from "@next-platform/client-common-core";
import { selectDisplayedMessageId } from "@next-platform/client-common-core";
import { CapabilityGate, SubCapabilityKey } from "@next-platform/client-common-core/infra/application/capabilities";
import React, { useMemo } from "react";

import RulebookHudContent from "./RulebookHudContent";
import RulebookHudErrorBoundary from "./RulebookHudErrorBoundary";

type RulebookHudProps = {
  characterId?: string;
  characterName?: string;
  conversationId?: string;
  conversation: IConversationMessage[];
  altMsgs: IConversationMessage[];
  altMsgPosition: number;
  isChatWithGroup: boolean;
  /** Forwarded to the snapshot hook — a brand-new chat counts as loaded. */
  isConversationLoaded?: boolean;
};

const RulebookHud = ({
  characterId,
  characterName,
  conversationId,
  conversation,
  altMsgs,
  altMsgPosition,
  isChatWithGroup,
  isConversationLoaded,
}: RulebookHudProps) => {
  // Per-reply reset: a crash on one message's snapshot hides only that reply, not the whole conversation.
  const displayedMessageId = useMemo(
    () => selectDisplayedMessageId({ conversation, altMsgs, altMsgPosition }),
    [conversation, altMsgs, altMsgPosition],
  );

  if (isChatWithGroup) {
    return null;
  }

  return (
    <RulebookHudErrorBoundary resetKey={`${conversationId ?? characterId ?? ""}:${displayedMessageId ?? ""}`}>
      <CapabilityGate capability={SubCapabilityKey.RulebookChatHud}>
        <RulebookHudContent
          key={characterId}
          characterId={characterId}
          characterName={characterName}
          conversationId={conversationId}
          conversation={conversation}
          altMsgs={altMsgs}
          altMsgPosition={altMsgPosition}
          isConversationLoaded={isConversationLoaded}
        />
      </CapabilityGate>
    </RulebookHudErrorBoundary>
  );
};

export default React.memo(RulebookHud);
